/**
 * YouTube Service — Real Implementation
 * Connects to the Reelia backend: upload → Whisper+GPT detection → clip extraction
 */
import { driveApi, processingApi } from './api';

export const VideoStatus = {
  UPLOADING: 'uploading',
  DETECTING: 'detecting',
  READY: 'ready',
  FAILED: 'failed',
};

export const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Upload a video file using chunked upload (1 MB chunks) to bypass proxy limits.
 * @param {File} file
 * @param {string} brandId
 * @param {string} creatorId
 * @param {function} onProgress - 0→100
 * @param {AbortSignal} signal - to cancel mid-upload
 */
export const uploadVideo = (file, brandId, creatorId, onProgress, signal) =>
  driveApi.uploadFileChunked(
    file,
    { brandId, creatorId, folder: 'yt_raw' },
    onProgress,
    signal,
  );

/**
 * Start async viral moment detection.
 * Returns a job object immediately; poll with pollJob().
 */
export const startDetectMoments = (videoId, count = 8) =>
  processingApi.detectMomentsAsync(videoId, count);

/**
 * Poll a processing job until it reaches status "done" or "error".
 * @param {string} jobId
 * @param {function} onProgress - called with progress (0-100)
 * @param {AbortSignal} signal - to cancel polling
 * @returns {Promise<object>} final job document
 */
export const pollJob = (jobId, onProgress, signal) =>
  new Promise((resolve, reject) => {
    const check = async () => {
      if (signal?.aborted) return reject(new Error('Cancelled'));
      try {
        const job = await processingApi.getJob(jobId);
        onProgress?.(job.progress ?? 0);
        if (job.status === 'done') return resolve(job);
        if (job.status === 'error') return reject(new Error(job.error_message || 'Job failed'));
        setTimeout(check, 3000);
      } catch (err) {
        // Retry on network errors
        setTimeout(check, 5000);
      }
    };
    check();
  });

/**
 * Map API segments → clip objects used by the UI.
 */
export const segmentsToClips = (segments, videoId) => {
  const BASE = process.env.REACT_APP_BACKEND_URL || '';
  return (segments || []).map((seg, i) => ({
    id: `clip-${videoId}-${i}`,
    title: seg.label || seg.reason || `Clip #${i + 1}`,
    startTime: seg.start,
    endTime: seg.end,
    duration: Math.round((seg.end - seg.start) * 10) / 10,
    viralScore: seg.score ?? 75,
    thumbnailUrl: `${BASE}/api/youtube/clip-thumbnail/${videoId}?time_seconds=${Math.floor(seg.start)}`,
    videoId,
  }));
};

/**
 * Extract a clip from a video (creates a clip_extract job).
 * Returns the job object; poll with pollJob() to get result_url.
 */
export const extractClip = (videoId, startTime, endTime, vertical = true) =>
  processingApi.createJob({
    video_id: videoId,
    job_type: 'clip_extract',
    params: { start: startTime, end: endTime, vertical },
  });
